import { useState, useEffect, useCallback } from "react";
import { ClipboardList, Loader2, Plus, Trash2, Check, Calendar, User } from "lucide-react";
import { apiFetch } from "@/portal/api";
import { useAuth } from "@/portal/AuthContext";
import { toast } from "@/components/ui/sonner";
import { confirmDialog } from "@/portal/confirm";
import DateInput from "@/portal/components/DateInput";

const today = () => new Date().toISOString().slice(0, 10);
const fmtDue = (s) => (s ? new Date(s + "T00:00:00").toLocaleDateString("cs-CZ", { day: "numeric", month: "short" }) : null);

const TasksTab = ({ token }) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", note: "", assignee_id: "", due_date: "" });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("open");

  const load = useCallback(async () => {
    try {
      const [t, m] = await Promise.all([
        apiFetch("/admin/tasks", { token }),
        apiFetch("/admin/team-members", { token }),
      ]);
      setTasks(t); setMembers(m);
    } catch (e) { /* */ } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Zadejte název úkolu."); return; }
    setSaving(true);
    try {
      const body = { title: form.title, note: form.note || null, assignee_id: form.assignee_id || null, due_date: form.due_date || null };
      await apiFetch("/admin/tasks", { method: "POST", token, body });
      toast.success("Úkol přidán."); setForm({ title: "", note: "", assignee_id: "", due_date: "" }); load();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };

  const toggle = async (t) => {
    try { await apiFetch(`/admin/tasks/${t.id}`, { method: "PATCH", token, body: { status: t.status === "done" ? "open" : "done" } }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const remove = async (t) => {
    if (!(await confirmDialog({ title: "Smazat úkol?", confirmText: "Smazat", danger: true }))) return;
    try { await apiFetch(`/admin/tasks/${t.id}`, { method: "DELETE", token }); toast.success("Smazáno."); load(); }
    catch (e) { toast.error(e.message); }
  };

  const td = today();
  const shown = tasks.filter((t) =>
    filter === "all" ? true
      : filter === "open" ? t.status !== "done"
        : filter === "mine" ? (t.assignee_id === user?.id && t.status !== "done")
          : t.status === "done");

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-orange-400 animate-spin" /></div>;

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="tasks-tab">
      <div className="lg:col-span-1">
        <div className="glass rounded-2xl p-6">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-4 inline-flex items-center gap-2"><Plus className="w-5 h-5 text-orange-400" /> Nový úkol</h3>
          <form onSubmit={add} className="space-y-3" data-testid="task-form">
            <input className="input-dark" placeholder="Název úkolu *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title" />
            <textarea className="input-dark" rows={2} placeholder="Detail (volitelné)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="task-note" />
            <select className="input-dark" value={form.assignee_id} onChange={(e) => setForm({ ...form, assignee_id: e.target.value })} data-testid="task-assignee">
              <option value="" className="bg-[#0a0a0a]">Bez přiřazení</option>
              {members.map((m) => <option key={m.id} value={m.id} className="bg-[#0a0a0a]">{m.name}{m.role === "admin" ? " (admin)" : ""}</option>)}
            </select>
            <DateInput value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} placeholder="Termín" testid="task-due" />
            <button type="submit" disabled={saving} className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="task-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Přidat úkol
            </button>
          </form>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold inline-flex items-center gap-2"><ClipboardList className="w-5 h-5 text-orange-400" /> Úkoly</h3>
          <div className="flex flex-wrap gap-1.5">
            {[["open", "Aktivní"], ["mine", "Moje"], ["done", "Hotové"], ["all", "Vše"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === k ? "border-orange-500 bg-orange-500/15 text-orange-300" : "border-white/10 text-zinc-400 hover:text-white"}`} data-testid={`tasks-filter-${k}`}>{l}</button>
            ))}
          </div>
        </div>
        {shown.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center text-zinc-500" data-testid="tasks-empty">Žádné úkoly v této kategorii.</div>
        ) : (
          <div className="space-y-2" data-testid="tasks-list">
            {shown.map((t) => {
              const overdue = t.due_date && t.status !== "done" && t.due_date < td;
              return (
                <div key={t.id} className={`glass rounded-xl p-4 flex items-start gap-3 ${t.status === "done" ? "opacity-50" : ""}`} data-testid={`task-${t.id}`}>
                  <button onClick={() => toggle(t)} className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${t.status === "done" ? "bg-emerald-500 border-emerald-500 text-white" : "border-white/20 hover:border-orange-400"}`} data-testid={`task-toggle-${t.id}`} aria-label="Označit hotovo">
                    {t.status === "done" && <Check className="w-3.5 h-3.5" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${t.status === "done" ? "line-through" : ""}`}>{t.title}</p>
                    {t.note && <p className="text-xs text-zinc-400 mt-0.5 break-words">{t.note}</p>}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px]">
                      {t.assignee_name && <span className="inline-flex items-center gap-1 text-zinc-400"><User className="w-3 h-3" /> {t.assignee_name}</span>}
                      {t.due_date && <span className={`inline-flex items-center gap-1 ${overdue ? "text-red-400" : "text-zinc-500"}`}><Calendar className="w-3 h-3" /> {fmtDue(t.due_date)}{overdue ? " · po termínu" : ""}</span>}
                    </div>
                  </div>
                  <button onClick={() => remove(t)} className="text-zinc-600 hover:text-red-400 p-1 shrink-0" data-testid={`task-delete-${t.id}`} aria-label="Smazat"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TasksTab;
